import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRecording } from "../api/recordings";
import PipelineProgressBar from "../components/PipelineProgressBar";
import StatusBadge, { ModeBadge } from "../components/StatusBadge";
import VideoViewer from "../components/VideoViewer";
import ScoreCard from "../components/ScoreCard";
import GateBanner from "../components/GateBanner";
import VideoStats from "../components/VideoStats";
import QcPanel from "../components/QcPanel";
import MetadataPanel from "../components/MetadataPanel";
import PiiChip from "../components/PiiChip";
import CategoryBadge from "../components/CategoryBadge";
import TaskCard from "../components/TaskCard";
import ApproveBar from "../components/ApproveBar";
import FraudBar from "../components/FraudBar";
import FraudCard from "../components/FraudCard";
import ClipComposer from "../components/ClipComposer";
import ClipsCard from "../components/ClipsCard";
import { formatDuration, recordingDate } from "../lib/format";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const recId = Number(id);
  const { data: rec, isLoading, error } = useRecording(recId);
  // clipping mode: the composer under the video and the ClipsCard share this state; the
  // video element is lifted here so the trim track can scrub it
  const [clipMode, setClipMode] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  if (Number.isNaN(recId)) return <p className="error">Invalid recording id.</p>;

  if (isLoading) {
    return (
      <div className="detail-page">
        <div className="detail-grid">
          <div className="skeleton viewer-skeleton" />
          <div className="detail-side">
            <div className="skeleton card-skeleton" />
            <div className="skeleton card-skeleton" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !rec) {
    return (
      <div className="empty-state">
        <p className="error">Recording not found.</p>
        <Link to="/" className="btn">
          ← Back to review
        </Link>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <header className="detail-header">
        <Link to="/" className="crumb">
          ← Review
        </Link>
        <div className="detail-title-row">
          {/* lead with the person; the log id stays in the subline / metadata panel */}
          <h1 className={rec.user_name ? undefined : "mono"} title={rec.log_id}>
            {rec.user_name ?? rec.log_id}
          </h1>
          <div className="detail-tags">
            <ModeBadge mode={rec.mode} />
            <StatusBadge status={rec.status} />
            <CategoryBadge task={rec.task} />
            <PiiChip pii={rec.qc?.pii} />
          </div>
        </div>
        <div className="detail-subline muted">
          {rec.user_name && (
            <>
              <span className="mono">{rec.log_id}</span>
              <span>·</span>
            </>
          )}
          <span title="When the recording was captured">
            {recordingDate(rec.log_id, rec.created_at)}
          </span>
          <span>·</span>
          <span>{rec.device_model ?? "unknown device"}</span>
          <span>·</span>
          <span>{formatDuration(rec.duration_ms)}</span>
          <span>·</span>
          <span>v{rec.app_version ?? "?"}</span>
          <span>·</span>
          <span>{rec.org}</span>
        </div>
        {rec.error && <p className="error detail-error">Error: {rec.error}</p>}
        {rec.qc && <GateBanner qc={rec.qc} mode={rec.mode} />}
        {rec.progress && (
          <div className="detail-progress">
            <PipelineProgressBar progress={rec.progress} />
          </div>
        )}
      </header>

      <div className="detail-grid">
        <div className="detail-main">
          <VideoViewer
            videoUrl={rec.video_url}
            blurredVideoUrl={rec.blurred_video_url}
            calibrationVideoUrl={rec.calibration_video_url}
            rrdUrl={rec.rrd_url}
            handsUrl={rec.hands_url}
            reportUrl={rec.report_url}
            durationMs={rec.duration_ms}
            pii={rec.qc?.pii}
            videoRef={videoRef}
          />
          {clipMode && (
            <ClipComposer rec={rec} videoRef={videoRef} onClose={() => setClipMode(false)} />
          )}
          {rec.qc && <VideoStats qc={rec.qc} />}
        </div>

        <aside className="detail-side">
          {rec.qc ? (
            <ScoreCard qc={rec.qc} mode={rec.mode} />
          ) : (
            <section className="card">
              <div className="card-head">
                <h3>Quality score</h3>
              </div>
              <p className="muted small">No QC results yet.</p>
            </section>
          )}
          <TaskCard task={rec.task} />
          <ClipsCard rec={rec} clipMode={clipMode} onStartClipping={() => setClipMode(true)} />
          {rec.fraud && <FraudCard fraud={rec.fraud} status={rec.status} />}
          <QcPanel qc={rec.qc} mode={rec.mode} />
          <MetadataPanel rec={rec} />
        </aside>
      </div>

      {rec.status === "potential_fraud" || rec.status === "fraud" ? (
        <FraudBar rec={rec} />
      ) : (
        <ApproveBar rec={rec} />
      )}
    </div>
  );
}
